import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  XMarkIcon as CloseIcon,
  ChevronDownIcon as ExpandMoreIcon,
  MagnifyingGlassIcon as SearchIcon,
  SparklesIcon,
  ClockIcon,
  ServerIcon,
  CpuChipIcon,
  WifiIcon,
  SignalIcon,
  BeakerIcon,
  CakeIcon,
  ArrowUpCircleIcon,
  CheckCircleIcon,
  ClockIcon as PendingIcon,
  InformationCircleIcon,
  RocketLaunchIcon,
  GlobeAltIcon,
  CloudIcon,
  EnvelopeIcon,
  MapPinIcon,
  ExclamationTriangleIcon,
  ClockIcon as HistoryIcon,
} from '@heroicons/react/24/outline';
import helpingQuestionsData from '../data/chatHelpingQuestions.json';
import ChatHistorySection from './ChatHistorySection';

// Icon mapping for categories
const categoryIcons = {
  connection: WifiIcon,
  history: ClockIcon,
  recent: ClockIcon,
  firmware: CpuChipIcon,
  gateway: ServerIcon,
  info: InformationCircleIcon,
  consumption: BeakerIcon,
  recipe: CakeIcon,
  upgrade: ArrowUpCircleIcon,
  update: CheckCircleIcon,
  pending: PendingIcon,
  deployment: RocketLaunchIcon,
  global: GlobeAltIcon,
  shadow: CloudIcon,
  message: EnvelopeIcon,
  market: MapPinIcon,
  error: ExclamationTriangleIcon,
};

export default function ChatHelpingQuestionsModal({ open, onClose, onQuestionSelect }) {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [currentTab, setCurrentTab] = useState(0); // 0: Helping Questions, 1: History

  const handleCategoryToggle = (categoryId) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleQuestionClick = (question) => {
    onQuestionSelect(question);
    onClose();
  };

  const handlePopularQuestionClick = (question) => {
    onQuestionSelect(question);
    onClose();
  };

  const handleQuickActionClick = (question) => {
    onQuestionSelect(question);
    onClose();
  };

  const handleHistoryQuestionSelect = (question) => {
    onQuestionSelect(question);
    onClose();
  };

  // Check if category is allowed on current route
  const isCategoryAllowed = (category) => {
    const currentPath = location.pathname;
    
    // If allowedRoutes contains "*", show on all routes
    if (category.allowedRoutes?.includes('*')) {
      return true;
    }
    
    // Check if current path matches any of the allowed routes
    return category.allowedRoutes?.some(route => {
      if (route === 'deviceOverview') {
        // For deviceOverview, only show if path contains deviceOverview AND has a serial number
        // Route format: /statistics/deviceOverview/:projectCode/:deviceSerial
        const pathParts = currentPath.split('/');
        const hasDeviceSerial = pathParts.length >= 5 && 
                               pathParts[1] === 'statistics' && 
                               pathParts[2] === 'deviceOverview' &&
                               pathParts[4]; // deviceSerial exists
        return hasDeviceSerial;
      }
      if (route === 'deviceOverview-no-serial') {
        // For deviceOverview-no-serial, only show if path contains deviceOverview BUT NO serial number
        // Route format: /statistics/deviceOverview (without :projectCode/:deviceSerial)
        const pathParts = currentPath.split('/');
        const isOnDeviceOverview = pathParts[1] === 'statistics' && pathParts[2] === 'deviceOverview';
        const hasDeviceSerial = pathParts.length >= 5 && pathParts[4]; // deviceSerial exists
        return isOnDeviceOverview && !hasDeviceSerial;
      }
      // For other routes, just check if path includes the route name
      return currentPath.includes(route);
    }) || false;
  };

  // Check if a specific question is allowed based on which category it belongs to
  const isQuestionAllowed = (questionText) => {
    // Find which category this question belongs to
    const category = helpingQuestionsData.categories.find(cat => 
      cat.questions.includes(questionText)
    );
    
    if (!category) {
      return false; // If question not found in any category, don't show it
    }
    
    return isCategoryAllowed(category);
  };

  // Filter categories and questions based on search term and allowed routes
  const filteredCategories = helpingQuestionsData.categories
    .filter(category => isCategoryAllowed(category))
    .map((category) => ({
      ...category,
      questions: category.questions.filter((question) =>
        question.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    }))
    .filter((category) => category.questions.length > 0);

  const filteredPopularQuestions = helpingQuestionsData.popularQuestions
    .filter((question) => isQuestionAllowed(question))
    .filter((question) => question.toLowerCase().includes(searchTerm.toLowerCase()));

  const filteredQuickActions = helpingQuestionsData.quickActions
    .filter((action) => isQuestionAllowed(action.question));

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[10000]"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-purple-500 to-purple-700 text-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col">
          
          {/* Header */}
          <div className="p-4 pb-2 flex-shrink-0">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <SparklesIcon className="h-6 w-6" />
                <h2 className="text-xl font-semibold">
                  {currentTab === 0 ? 'Frequently Asked Questions' : 'Conversation History'}
                </h2>
              </div>
              <button 
                onClick={onClose}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-1 transition-colors"
              >
                <CloseIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b-2 border-white">
              <button
                onClick={() => setCurrentTab(0)}
                className={`flex items-center gap-2 px-4 py-2 transition-colors relative ${
                  currentTab === 0 
                    ? 'text-white font-bold' 
                    : 'text-white text-opacity-60'
                }`}
              >
                <SparklesIcon className="h-5 w-5" />
                <span className="text-sm">Suggested Questions</span>
                {currentTab === 0 && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-white rounded-t" />
                )}
              </button>
              <button
                onClick={() => setCurrentTab(1)}
                className={`flex items-center gap-2 px-4 py-2 transition-colors relative ${
                  currentTab === 1 
                    ? 'text-white font-bold' 
                    : 'text-white text-opacity-60'
                }`}
              >
                <HistoryIcon className="h-5 w-5" />
                <span className="text-sm">My History</span>
                {currentTab === 1 && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-white rounded-t" />
                )}
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 pt-0">
            {currentTab === 0 ? (
              // Frequently Asked Questions Tab
              <>
                {/* Search Bar */}
                <div className="pb-4 pt-4">
                  <div className="relative">
                    <SearchIcon className="h-5 w-5 absolute left-3 top-1/2 -translate-y-1/2 text-purple-600" />
                    <input
                      type="text"
                      placeholder="Search questions..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full bg-white rounded-lg pl-10 pr-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-white"
                    />
                  </div>
                </div>

                {/* Quick Actions */}
                {!searchTerm && filteredQuickActions.length > 0 && (
                  <div className="mb-4">
                    <h3 className="mb-3 font-bold flex items-center gap-2 text-sm">
                      <SparklesIcon className="h-5 w-5" />
                      Quick Actions
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {filteredQuickActions.map((action, index) => (
                        <button
                          key={index}
                          onClick={() => handleQuickActionClick(action.question)}
                          className="bg-white bg-opacity-20 text-white px-3 py-1.5 rounded-full text-sm font-bold border border-white border-opacity-30 hover:bg-opacity-30 hover:scale-105 transition-all cursor-pointer"
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Popular Questions */}
                {filteredPopularQuestions.length > 0 && !searchTerm && (
                  <div className="mb-4">
                    <h3 className="mb-3 font-bold flex items-center gap-2 text-sm">
                      <SignalIcon className="h-5 w-5" />
                      Popular Questions
                    </h3>
                    <div className="flex flex-col gap-2">
                      {filteredPopularQuestions.slice(0, 5).map((question, index) => (
                        <button
                          key={index}
                          onClick={() => handlePopularQuestionClick(question)}
                          className="p-3 bg-white bg-opacity-15 rounded-lg border border-white border-opacity-20 hover:bg-opacity-25 hover:translate-x-1 transition-all text-left"
                        >
                          <p className="text-sm">{question}</p>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Categories */}
                <div>
                  <h3 className="mb-3 font-bold flex items-center gap-2 text-sm">
                    <InformationCircleIcon className="h-5 w-5" />
                    {searchTerm ? 'Search Results' : 'All Categories'}
                  </h3>

                  {filteredCategories.length === 0 ? (
                    <p className="text-center py-6 text-sm text-white text-opacity-70">
                      No questions found matching &quot;{searchTerm}&quot;
                    </p>
                  ) : (
                    filteredCategories.map((category) => {
                      const IconComponent = categoryIcons[category.icon] || InformationCircleIcon;
                      const isExpanded = expandedCategories.includes(category.id) || searchTerm !== '';
                      
                      return (
                        <div
                          key={category.id}
                          className="bg-white bg-opacity-10 rounded-lg border border-white border-opacity-20 mb-2"
                        >
                          {/* Accordion Header */}
                          <button
                            onClick={() => handleCategoryToggle(category.id)}
                            className="w-full px-4 py-3 flex items-center justify-between hover:bg-white hover:bg-opacity-5 transition-colors rounded-lg"
                          >
                            <div className="flex items-center gap-2 flex-1">
                              <IconComponent className="h-5 w-5" />
                              <span className="font-bold">{category.title}</span>
                              <span className="bg-white bg-opacity-20 text-white text-xs px-2 py-0.5 rounded ml-auto">
                                {category.questions.length}
                              </span>
                            </div>
                            <ExpandMoreIcon 
                              className={`h-5 w-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                            />
                          </button>

                          {/* Accordion Content */}
                          {isExpanded && (
                            <div className="px-4 pb-3 pt-0">
                              <div className="flex flex-col gap-2">
                                {category.questions.map((question, qIndex) => (
                                  <button
                                    key={qIndex}
                                    onClick={() => handleQuestionClick(question)}
                                    className="p-2.5 bg-white bg-opacity-5 rounded border border-white border-opacity-10 hover:bg-opacity-15 hover:translate-x-1 hover:border-opacity-30 transition-all text-left"
                                  >
                                    <p className="text-sm">{question}</p>
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            ) : (
              // History Tab
              <ChatHistorySection onQuestionSelect={handleHistoryQuestionSelect} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
